// components/auth/Register.jsx
import React, { useState } from 'react';
import { prepareRegistrationData } from '../../utils/crypto';
import config from '@/config';

const Register = ({ projectId, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [showRecoveryKey, setShowRecoveryKey] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      // Prepare encrypted data on client side
      const registrationData = await prepareRegistrationData(
        formData.email,
        formData.password,
        projectId
      );

      // Store recovery key to show user
      setRecoveryKey(registrationData.recoveryKey);

      // Remove recovery key from data sent to server (only send hash)
      const { recoveryKey: _, ...dataToSend } = registrationData;

// In your Register component, before the fetch call:
console.log("Sending registration data:", dataToSend);

const response = await fetch(`${config.AUTH_API_ADDRESS}auth/register`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(dataToSend),
});

// Add this to see the error details:
if (!response.ok) {
  const errorData = await response.json();
  console.error("Backend error:", errorData);
  throw new Error(errorData.detail || 'Registration failed');
}


      // Registration successful, show recovery key
      setShowRecoveryKey(true);

    } catch (err) {
      setError(err.message || 'Registration failed');
      setLoading(false);
    }
  };

  const handleRecoveryKeySaved = () => {
    // Store tokens and redirect
    if (onSuccess) {
      onSuccess();
    }
  };

  const copyRecoveryKey = () => {
    navigator.clipboard.writeText(recoveryKey);
    alert('Recovery key copied to clipboard!');
  };

  if (showRecoveryKey) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="text-center mb-6">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Save Your Recovery Key
          </h2>
          <p className="text-gray-600">
            This is the ONLY time you will see this key. Save it securely!
          </p>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-700 mb-2 font-semibold">Your Recovery Key:</p>
          <div className="bg-white p-3 rounded border border-gray-300 font-mono text-sm break-all">
            {recoveryKey}
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <button
            onClick={copyRecoveryKey}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
          >
            📋 Copy to Clipboard
          </button>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-800">
            ⚠️ Without this key, you cannot reset your password. Store it in a secure location.
          </p>
        </div>

        <button
          onClick={handleRecoveryKeySaved}
          className="w-full bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 font-semibold"
        >
          I have saved my recovery key
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
        Create Account
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            minLength={8}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Min. 8 characters"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password
          </label>
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Confirm password"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 disabled:bg-gray-400 font-semibold"
        >
          {loading ? 'Creating Account...' : 'Register'}
        </button>
      </form>

      <p className="text-center text-sm text-gray-600 mt-4">
        Already have an account?{' '}
        <a href="/login" className="text-blue-500 hover:underline">
          Login
        </a>
      </p>
    </div>
  );
};

export default Register;